import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { showCustomToast } from '../components/common/CustomToast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import BanUserModal from '../components/modals/BanUserModal';
import NotFoundPage from './NotFoundPage';
import './AdminPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface HouseWalletData {
  balance: number;
  balanceLamports: number;
  address: string;
  network: string;
  explorerUrl: string;
}

interface Withdrawal {
  id: string;
  userId: string;
  amount: string;
  solanaAddress: string;
  status: 'pending' | 'approved' | 'declined';
  createdAt: string;
  processedAt?: string;
  adminNote?: string;
  user: {
    username: string;
    email: string;
  };
}

interface UserPagination {
  currentPage: number;
  totalPages: number;
  totalUsers: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

const AdminPage: React.FC = () => {
  const { token, user } = useSelector((state: RootState) => state.auth);
  const [houseWallet, setHouseWallet] = useState<HouseWalletData | null>(null);
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({
    toAddress: '',
    amount: ''
  });
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [declineNote, setDeclineNote] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<'overview' | 'withdraw' | 'withdrawals' | 'vouchers' | 'gamemodes' | 'users'>('overview');
  const [vouchers, setVouchers] = useState<any[]>([]);
  const [voucherForm, setVoucherForm] = useState({
    code: '',
    amount: '',
    maxRedemptions: ''
  });
  const [creatingVoucher, setCreatingVoucher] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [gameModeStatus, setGameModeStatus] = useState({ jackpot: false, coinflip: false });
  const [users, setUsers] = useState<any[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userPagination, setUserPagination] = useState<UserPagination>({
    currentPage: 1,
    totalPages: 1,
    totalUsers: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [banModalOpen, setBanModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{id: string, username: string} | null>(null);
  const itemsPerPage = 10;

  const fetchHouseWalletBalance = useCallback(async (showLoader = false) => {
    if (showLoader) setRefreshing(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/house-wallet-balance`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setHouseWallet(data);
      } else if (response.status === 403) {
        showCustomToast.error('Access denied: Admin privileges required');
      } else {
        showCustomToast.error('Failed to fetch house wallet balance');
      }
    } catch (error) {
      console.error('Error fetching house wallet balance:', error);
      showCustomToast.error('Error fetching house wallet balance');
    } finally {
      setLoading(false);
      if (showLoader) setRefreshing(false);
    }
  }, [token]);

  const fetchWithdrawals = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/withdrawals?status=pending`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWithdrawals(data);
      }
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
    }
  }, [token]);

  const fetchVouchers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/vouchers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVouchers(data);
      }
    } catch (error) {
      console.error('Error fetching vouchers:', error);
    }
  }, [token]);

  const fetchUsers = useCallback(async (page = 1, search = '') => {
    setLoadingUsers(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '5',
        search: search
      });
      
      const response = await fetch(`${API_BASE_URL}/api/admin/users?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setUserPagination(data.pagination);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  }, [token]);

  const fetchMaintenanceStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/maintenance`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMaintenanceMode(data.enabled);
      }
    } catch (error) {
      console.error('Error fetching maintenance status:', error);
    }
  }, [token]);

  const fetchGameModeStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/gamemode-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setGameModeStatus(data);
      }
    } catch (error) {
      console.error('Error fetching gamemode status:', error);
    }
  }, [token]);

  useEffect(() => {
    if (user?.isAdmin) {
      fetchHouseWalletBalance();
      fetchWithdrawals();
      fetchVouchers();
      fetchUsers();
      fetchMaintenanceStatus();
      fetchGameModeStatus();
    } else {
      setLoading(false);
    }
  }, [user?.isAdmin, fetchHouseWalletBalance, fetchWithdrawals, fetchVouchers, fetchUsers, fetchMaintenanceStatus, fetchGameModeStatus]);

  // Handle user search with debouncing
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      fetchUsers(1, userSearchTerm);
    }, 300); // 300ms debounce
    
    setSearchTimeout(timeout);
    
    return () => {
      if (timeout) clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSearchTerm, fetchUsers]);

  // Check admin access after hooks
  if (!user?.isAdmin) {
    return <NotFoundPage type="403" />;
  }

  const handleToggleGameMode = async (gamemode: 'jackpot' | 'coinflip') => {
    try {
      const newStatus = !gameModeStatus[gamemode];
      
      const response = await fetch(`${API_BASE_URL}/api/admin/gamemode-status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ gamemode, disabled: newStatus })
      });

      if (response.ok) {
        setGameModeStatus(prev => ({ ...prev, [gamemode]: newStatus }));
        showCustomToast.success(`${gamemode} ${newStatus ? 'disabled' : 'enabled'}`);
      } else {
        showCustomToast.error('Failed to toggle gamemode');
      }
    } catch (error) {
      console.error('Toggle gamemode error:', error);
      showCustomToast.error('Failed to toggle gamemode');
    }
  };

  const handleApproveWithdrawal = async (withdrawalId: string) => {
    setProcessingId(withdrawalId);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/withdrawals/${withdrawalId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          adminNote: 'Approved by admin'
        })
      });

      const data = await response.json();

      if (response.ok) {
        showCustomToast.success('Withdrawal approved successfully!');
        fetchWithdrawals();
        fetchHouseWalletBalance();
      } else {
        showCustomToast.error(data.error || 'Failed to approve withdrawal');
      }
    } catch (error) {
      console.error('Approval error:', error);
      showCustomToast.error('Failed to approve withdrawal');
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeclineWithdrawal = async (withdrawalId: string) => {
    const note = declineNote[withdrawalId];
    
    if (!note || note.trim() === '') {
      showCustomToast.error('Please enter a reason for declining');
      return;
    }

    setProcessingId(withdrawalId);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/withdrawals/${withdrawalId}/decline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          adminNote: note
        })
      });

      const data = await response.json();

      if (response.ok) {
        showCustomToast.success('Withdrawal declined and balance refunded');
        fetchWithdrawals();
        setDeclineNote(prev => {
          const updated = { ...prev };
          delete updated[withdrawalId];
          return updated;
        });
      } else {
        showCustomToast.error(data.error || 'Failed to decline withdrawal');
      }
    } catch (error) {
      console.error('Decline error:', error);
      showCustomToast.error('Failed to decline withdrawal');
    } finally {
      setProcessingId(null);
    }
  };

  const handleCreateVoucher = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!voucherForm.code || !voucherForm.amount || !voucherForm.maxRedemptions) {
      showCustomToast.error('Please fill in all fields');
      return;
    }

    setCreatingVoucher(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/vouchers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          code: voucherForm.code,
          amount: parseFloat(voucherForm.amount),
          maxRedemptions: parseInt(voucherForm.maxRedemptions)
        })
      });

      const data = await response.json();

      if (response.ok) {
        showCustomToast.success('Voucher created successfully!');
        setVoucherForm({ code: '', amount: '', maxRedemptions: '' });
        fetchVouchers();
      } else {
        showCustomToast.error(data.error || 'Failed to create voucher');
      }
    } catch (error) {
      console.error('Create voucher error:', error);
      showCustomToast.error('Failed to create voucher');
    } finally {
      setCreatingVoucher(false);
    }
  };

  const handleToggleVoucher = async (voucherId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/vouchers/${voucherId}/toggle`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        showCustomToast.success('Voucher status updated');
        fetchVouchers();
      } else {
        showCustomToast.error('Failed to update voucher');
      }
    } catch (error) {
      console.error('Toggle voucher error:', error);
      showCustomToast.error('Failed to update voucher');
    }
  };

  const handleDeleteVoucher = async (voucherId: string) => {
    if (!window.confirm('Are you sure you want to delete this voucher?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/vouchers/${voucherId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showCustomToast.success('Voucher deleted');
        fetchVouchers();
      } else {
        showCustomToast.error('Failed to delete voucher');
      }
    } catch (error) {
      console.error('Delete voucher error:', error);
      showCustomToast.error('Failed to delete voucher');
    }
  };

  const handleBanUser = async (reason: string, duration: number) => {
    if (!selectedUser) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${selectedUser.id}/ban`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reason, duration })
      });

      if (response.ok) {
        showCustomToast.success('User banned successfully');
        setBanModalOpen(false);
        setSelectedUser(null);
        fetchUsers(userPagination.currentPage, userSearchTerm);
      } else {
        const data = await response.json();
        showCustomToast.error(data.error || 'Failed to ban user');
      }
    } catch (error) {
      console.error('Ban user error:', error);
      showCustomToast.error('Failed to ban user');
    }
  };

  const handleUnbanUser = async (userId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/unban`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showCustomToast.success('User unbanned successfully');
        fetchUsers(userPagination.currentPage, userSearchTerm);
      } else {
        showCustomToast.error('Failed to unban user');
      }
    } catch (error) {
      console.error('Unban user error:', error);
      showCustomToast.error('Failed to unban user');
    }
  };

  const handleToggleReview = async (userId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/review`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showCustomToast.success('User review status updated');
        fetchUsers(userPagination.currentPage, userSearchTerm);
      } else {
        showCustomToast.error('Failed to update review status');
      }
    } catch (error) {
      console.error('Toggle review error:', error);
      showCustomToast.error('Failed to update review status');
    }
  };

  const handleToggleMaintenance = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/maintenance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ enabled: !maintenanceMode })
      });

      if (response.ok) {
        const newMaintenanceMode = !maintenanceMode;
        setMaintenanceMode(newMaintenanceMode);
        showCustomToast.success(`Maintenance mode ${newMaintenanceMode ? 'enabled' : 'disabled'}`);
        
        // If maintenance mode was just enabled, redirect non-admin users to waiting page
        if (newMaintenanceMode) {
          // Force a brief delay to ensure the toast is visible, then redirect
          setTimeout(() => {
            window.location.href = '/waiting';
          }, 1000);
        }
      } else {
        showCustomToast.error('Failed to toggle maintenance mode');
      }
    } catch (error) {
      console.error('Toggle maintenance error:', error);
      showCustomToast.error('Failed to toggle maintenance mode');
    }
  };

  const handleWithdraw = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!withdrawForm.toAddress || !withdrawForm.amount || parseFloat(withdrawForm.amount) <= 0) {
      showCustomToast.error('Please enter valid address and amount');
      return;
    }

    setWithdrawing(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/withdraw-house-funds`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          toAddress: withdrawForm.toAddress,
          amount: parseFloat(withdrawForm.amount)
        })
      });

      const data = await response.json();

      if (response.ok) {
        showCustomToast.success(`Successfully withdrew ${withdrawForm.amount} SOL!`);
        setWithdrawForm({ toAddress: '', amount: '' });
        fetchHouseWalletBalance();
      } else {
        showCustomToast.error(data.error || 'Withdrawal failed');
      }
    } catch (error) {
      console.error('Withdrawal error:', error);
      showCustomToast.error('Withdrawal failed');
    } finally {
      setWithdrawing(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-container">
          <div className="loading-state">
            <LoadingSpinner text="Loading Admin Panel..." size="large" />
            <p style={{ color: '#999', marginTop: '8px' }}>Please wait while we fetch your data</p>
          </div>
        </div>
      </div>
    );
  }

  // Filter and paginate withdrawals
  const filteredWithdrawals = withdrawals.filter(w => 
    w.user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.solanaAddress.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredWithdrawals.length / itemsPerPage);
  const paginatedWithdrawals = filteredWithdrawals.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="admin-page">
      <div className="admin-container">
        {/* Header - Title Only */}
        <div className="admin-header-modern">
          <div className="header-left">
            <div className="header-text">
              <h1>Dashboard</h1>
              <p>Welcome back, <span className="username-highlight">{user?.username}</span></p>
            </div>
          </div>
          <div className="header-right">
            <div className="network-indicator">
              <div className="status-dot"></div>
              <span>{houseWallet?.network || 'devnet'}</span>
            </div>
            <button 
              className="refresh-btn" 
              onClick={() => {
                fetchHouseWalletBalance(true);
                fetchWithdrawals();
                fetchVouchers();
                fetchUsers(userPagination.currentPage, userSearchTerm);
                fetchGameModeStatus();
              }}
              disabled={refreshing}
              title="Refresh data"
            >
              <svg 
                className={refreshing ? 'spinning' : ''} 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <polyline points="23 4 23 10 17 10"/>
                <polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="modern-tabs">
          <button 
            className={`modern-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`modern-tab ${activeTab === 'withdraw' ? 'active' : ''}`}
            onClick={() => setActiveTab('withdraw')}
          >
            Withdraw
          </button>
          <button 
            className={`modern-tab ${activeTab === 'withdrawals' ? 'active' : ''}`}
            onClick={() => setActiveTab('withdrawals')}
          >
            Requests
            {withdrawals.length > 0 && (
              <span className="tab-badge">{withdrawals.length}</span>
            )}
          </button>
          <button 
            className={`modern-tab ${activeTab === 'vouchers' ? 'active' : ''}`}
            onClick={() => setActiveTab('vouchers')}
          >
            Vouchers
          </button>
          <button 
            className={`modern-tab ${activeTab === 'gamemodes' ? 'active' : ''}`}
            onClick={() => setActiveTab('gamemodes')}
          >
            Gamemodes
          </button>
          <button 
            className={`modern-tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Users
          </button>
        </div>

        {/* Tab Content - Overview */}
        {activeTab === 'overview' && (
          <div className="tab-panel">
            <div className="glass-card">
              <div className="glass-card-header">
                <h3>Site Maintenance</h3>
              </div>
              <div className="glass-card-body">
                <div className="maintenance-section">
                  <label className="maintenance-label">Maintenance Mode</label>
                  <button 
                    className={`maintenance-toggle ${maintenanceMode ? 'active' : 'inactive'}`}
                    onClick={handleToggleMaintenance}
                  >
                    {maintenanceMode ? 'Enabled' : 'Disabled'}
                  </button>
                  <p className="maintenance-description">
                    When enabled, all users will be redirected to a waiting page
                  </p>
                </div>
              </div>
            </div>

            <div className="glass-card">
              <div className="glass-card-header">
                <h3>House Wallet</h3>
              </div>
              <div className="glass-card-body">
                <div className="wallet-compact">
                  <div className="wallet-row">
                    <span className="wallet-label">Address</span>
                    <div className="wallet-value">
                      <code>{houseWallet?.address.slice(0, 8)}...{houseWallet?.address.slice(-8)}</code>
                      <button
                        className="copy-icon-btn"
                        onClick={() => {
                          navigator.clipboard.writeText(houseWallet?.address || '');
                          showCustomToast.success('Address copied!');
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="wallet-row">
                    <span className="wallet-label">Balance</span>
                    <span className="wallet-balance">{houseWallet?.balance.toFixed(4)} SOL</span>
                  </div>
                  <div className="wallet-row">
                    <span className="wallet-label">Explorer</span>
                    <a 
                      href={houseWallet?.explorerUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="explorer-link"
                    >
                      View Transactions
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content - Withdraw */}
        {activeTab === 'withdraw' && (
          <div className="tab-panel">
            <div className="glass-card">
              <div className="glass-card-header">
                <h3>Withdraw House Funds</h3>
              </div>
              <div className="glass-card-body">
                <form onSubmit={handleWithdraw} className="modern-form">
                  <div className="modern-form-group">
                    <label htmlFor="toAddress">
                      Destination Address
                      <span className="required-indicator">*</span>
                    </label>
                    <input
                      type="text"
                      id="toAddress"
                      className="modern-input"
                      value={withdrawForm.toAddress}
                      onChange={(e) => setWithdrawForm({...withdrawForm, toAddress: e.target.value})}
                      placeholder="Enter Solana wallet address"
                      required
                    />
                  </div>

                  <div className="modern-form-group">
                    <label htmlFor="amount">
                      Amount (SOL)
                      <span className="required-indicator">*</span>
                    </label>
                    <div className="input-with-action">
                      <input
                        type="number"
                        id="amount"
                        className="modern-input"
                        step="0.000001"
                        min="0.000001"
                        value={withdrawForm.amount}
                        onChange={(e) => setWithdrawForm({...withdrawForm, amount: e.target.value})}
                        placeholder="0.0000"
                        required
                      />
                      <button
                        type="button"
                        className="max-btn-inline"
                        onClick={() => setWithdrawForm({...withdrawForm, amount: houseWallet?.balance.toString() || ''})}
                      >
                        MAX
                      </button>
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    className="place-bet-btn"
                    disabled={withdrawing}
                  >
                    {withdrawing ? (
                      <>
                        <div className="btn-spinner"></div>
                        Processing Transaction...
                      </>
                    ) : (
                      'Withdraw Funds'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content - Withdrawals */}
        {activeTab === 'withdrawals' && (
          <div className="tab-panel">
            <div className="glass-card requests-card">
              <div className="glass-card-header">
                <h3>Withdrawal Requests</h3>
                <div className="modern-search">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="m21 21-4.35-4.35"/>
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by username, email, or address..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="modern-search-input"
                  />
                </div>
              </div>

              <div className="glass-card-body no-padding">
                {filteredWithdrawals.length === 0 ? (
                  <div className="modern-empty-state">
                    <h4>No Withdrawal Requests</h4>
                    <p>{searchTerm ? 'No results found for your search' : 'All withdrawal requests have been processed'}</p>
                  </div>
                ) : (
                  <>
                    <div className="modern-table-wrapper">
                      <table className="modern-table">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Amount</th>
                            <th>Destination</th>
                            <th>Date</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedWithdrawals.map((withdrawal) => (
                            <tr key={withdrawal.id} className={processingId === withdrawal.id ? 'processing' : ''}>
                              <td>
                                <div className="user-info-cell">
                                  <div>
                                    <div className="user-name">{withdrawal.user.username}</div>
                                    <div className="user-email">{withdrawal.user.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div className="amount-display">
                                  {(Number(withdrawal.amount) / 1_000_000_000).toFixed(4)} SOL
                                </div>
                              </td>
                              <td>
                                <div className="address-cell-modern">
                                  <code>{withdrawal.solanaAddress.slice(0, 6)}...{withdrawal.solanaAddress.slice(-6)}</code>
                                  <button
                                    className="copy-icon-btn"
                                    onClick={() => {
                                      navigator.clipboard.writeText(withdrawal.solanaAddress);
                                      showCustomToast.success('Address copied!');
                                    }}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                                    </svg>
                                  </button>
                                </div>
                              </td>
                              <td>
                                <div className="date-display">
                                  {new Date(withdrawal.createdAt).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                  <span className="time-display">
                                    {new Date(withdrawal.createdAt).toLocaleTimeString('en-US', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <div className="action-cell">
                                  <button
                                    className="clean-btn approve"
                                    onClick={() => handleApproveWithdrawal(withdrawal.id)}
                                    disabled={processingId === withdrawal.id}
                                  >
                                    {processingId === withdrawal.id ? (
                                      <div className="btn-spinner-small"></div>
                                    ) : (
                                      'Approve'
                                    )}
                                  </button>
                                  <button
                                    className="clean-btn decline"
                                    onClick={() => {
                                      const note = prompt('Enter reason for declining:');
                                      if (note && note.trim()) {
                                        setDeclineNote({...declineNote, [withdrawal.id]: note});
                                        handleDeclineWithdrawal(withdrawal.id);
                                      } else if (note !== null) {
                                        showCustomToast.error('Please enter a reason for declining');
                                      }
                                    }}
                                    disabled={processingId === withdrawal.id}
                                  >
                                    Decline
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {totalPages > 1 && (
                      <div className="modern-pagination">
                        <button
                          className="pagination-btn"
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="15 18 9 12 15 6"/>
                          </svg>
                          Previous
                        </button>

                        <div className="pagination-info">
                          <span>Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong></span>
                          <span className="pagination-total">({filteredWithdrawals.length} {filteredWithdrawals.length === 1 ? 'request' : 'requests'})</span>
                        </div>

                        <button
                          className="pagination-btn"
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                        >
                          Next
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6"/>
                          </svg>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tab Content - Gamemodes */}
        {activeTab === 'gamemodes' && (
          <div className="tab-panel">
            <div className="glass-card">
              <div className="glass-card-header">
                <h3>Gamemode Status</h3>
              </div>
              <div className="glass-card-body">
                <div className="gamemode-controls">
                  <div className="gamemode-item">
                    <div className="gamemode-info">
                      <h4>Jackpot</h4>
                      <p>Main jackpot game</p>
                    </div>
                    <button 
                      className={`maintenance-toggle ${gameModeStatus.jackpot ? 'inactive' : 'active'}`}
                      onClick={() => handleToggleGameMode('jackpot')}
                    >
                      {gameModeStatus.jackpot ? 'Disabled' : 'Enabled'}
                    </button>
                  </div>
                  <div className="gamemode-item">
                    <div className="gamemode-info">
                      <h4>Coinflip</h4>
                      <p>1v1 coinflip battles</p>
                    </div>
                    <button 
                      className={`maintenance-toggle ${gameModeStatus.coinflip ? 'inactive' : 'active'}`}
                      onClick={() => handleToggleGameMode('coinflip')}
                    >
                      {gameModeStatus.coinflip ? 'Disabled' : 'Enabled'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content - Users */}
        {activeTab === 'users' && (
          <div className="tab-panel">
            <div className="glass-card">
              <div className="glass-card-header">
                <h3>User Management</h3>
                <div className="modern-search">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="m21 21-4.35-4.35"/>
                  </svg>
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className="modern-search-input"
                  />
                </div>
              </div>
              <div className="glass-card-body no-padding">
                {loadingUsers ? (
                  <div className="modern-empty-state">
                    <LoadingSpinner text="Loading users..." size="medium" />
                  </div>
                ) : users.length === 0 ? (
                  <div className="modern-empty-state">
                    <h4>No Users Found</h4>
                    <p>{userSearchTerm ? 'No users match your search criteria' : 'No users available'}</p>
                  </div>
                ) : (
                  <>
                    <div className="user-list">
                      {users.map((user) => (
                        <div key={user.id} className="user-item">
                          <div className="user-info">
                            <div className="user-name">{user.username}</div>
                            <div className="user-email">{user.email}</div>
                            <div className="user-status">
                              {user.isBanned ? (
                                <span className="status-badge banned">Banned</span>
                              ) : user.isUnderReview ? (
                                <span className="status-badge review">Under Review</span>
                              ) : (
                                <span className="status-badge active">Active</span>
                              )}
                            </div>
                          </div>
                          <div className="user-actions">
                            {!user.isBanned && (
                              <>
                                <button
                                  className="clean-btn decline"
                                  onClick={() => {
                                    setSelectedUser({ id: user.id, username: user.username });
                                    setBanModalOpen(true);
                                  }}
                                >
                                  Ban User
                                </button>
                                <button
                                  className={`clean-btn ${user.isUnderReview ? 'approve' : 'decline'}`}
                                  onClick={() => handleToggleReview(user.id)}
                                >
                                  {user.isUnderReview ? 'Remove Review' : 'Mark Under Review'}
                                </button>
                              </>
                            )}
                            {user.isBanned && (
                              <button
                                className="clean-btn approve"
                                onClick={() => handleUnbanUser(user.id)}
                              >
                                Unban User
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* User Pagination */}
                    {userPagination.totalPages > 1 && (
                      <div className="modern-pagination">
                        <button
                          className="pagination-btn"
                          onClick={() => fetchUsers(userPagination.currentPage - 1, userSearchTerm)}
                          disabled={!userPagination.hasPrevPage || loadingUsers}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="15 18 9 12 15 6"/>
                          </svg>
                          Previous
                        </button>

                        <div className="pagination-info">
                          <span>Page <strong>{userPagination.currentPage}</strong> of <strong>{userPagination.totalPages}</strong></span>
                          <span className="pagination-total">({userPagination.totalUsers} {userPagination.totalUsers === 1 ? 'user' : 'users'})</span>
                        </div>

                        <button
                          className="pagination-btn"
                          onClick={() => fetchUsers(userPagination.currentPage + 1, userSearchTerm)}
                          disabled={!userPagination.hasNextPage || loadingUsers}
                        >
                          Next
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6"/>
                          </svg>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            
          </div>
        )}

        {/* Ban User Modal */}
        <BanUserModal
          isOpen={banModalOpen}
          onClose={() => {
            setBanModalOpen(false);
            setSelectedUser(null);
          }}
          onBan={handleBanUser}
          username={selectedUser?.username || ''}
        />

        {/* Tab Content - Vouchers */}
        {activeTab === 'vouchers' && (
          <div className="tab-panel">
            <div className="glass-card">
              <div className="glass-card-header">
                <h3>Create Voucher</h3>
              </div>
              <div className="glass-card-body">
                <form onSubmit={handleCreateVoucher} className="modern-form">
                  <div className="form-row">
                    <div className="modern-form-group">
                      <label>Voucher Code</label>
                      <input
                        type="text"
                        className="modern-input"
                        value={voucherForm.code}
                        onChange={(e) => setVoucherForm({...voucherForm, code: e.target.value.toUpperCase()})}
                        placeholder="e.g., BETA"
                        required
                      />
                    </div>
                    <div className="modern-form-group">
                      <label>Amount (SOL)</label>
                      <input
                        type="number"
                        step="0.001"
                        min="0.001"
                        className="modern-input"
                        value={voucherForm.amount}
                        onChange={(e) => setVoucherForm({...voucherForm, amount: e.target.value})}
                        placeholder="0.1"
                        required
                      />
                    </div>
                    <div className="modern-form-group">
                      <label>Max Redemptions</label>
                      <input
                        type="number"
                        min="1"
                        className="modern-input"
                        value={voucherForm.maxRedemptions}
                        onChange={(e) => setVoucherForm({...voucherForm, maxRedemptions: e.target.value})}
                        placeholder="5"
                        required
                      />
                    </div>
                  </div>
                  <button type="submit" className="place-bet-btn" disabled={creatingVoucher}>
                    {creatingVoucher ? 'Creating...' : 'Create Voucher'}
                  </button>
                </form>
              </div>
            </div>

            <div className="glass-card">
              <div className="glass-card-header">
                <h3>Vouchers</h3>
              </div>
              <div className="glass-card-body no-padding">
                {vouchers.length === 0 ? (
                  <div className="modern-empty-state">
                    <h4>No Vouchers</h4>
                    <p>Create your first voucher above</p>
                  </div>
                ) : (
                  <div className="voucher-list">
                    {vouchers.map((voucher) => (
                      <div key={voucher.id} className="voucher-item">
                        <div className="voucher-info">
                          <div className="voucher-code">{voucher.code}</div>
                          <div className="voucher-details">
                            <span>{(Number(voucher.amount) / 1_000_000_000).toFixed(4)} SOL</span>
                            <span>{voucher.currentRedemptions}/{voucher.maxRedemptions} used</span>
                            <span className={`voucher-status ${voucher.isActive ? 'active' : 'inactive'}`}>
                              {voucher.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                        <div className="voucher-actions">
                          <button
                            className={`clean-btn ${voucher.isActive ? 'decline' : 'approve'}`}
                            onClick={() => handleToggleVoucher(voucher.id)}
                          >
                            {voucher.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            className="clean-btn decline"
                            onClick={() => handleDeleteVoucher(voucher.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;